import { useState } from "react";
import styled from "styled-components";
import { Input, Modal } from "antd";
import { DeleteOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { postComment, putComment, deleteComment, getSlideData } from "../../api/slideAPI";
import { checkUser } from "../../api/authAPI";

interface CommentPanelProps {
  slideId?: number;
  slideComments: any[];
  onCommentsChange: (comments: any[]) => void;
  onClose: () => void;
}

export const CommentPanel = ({ slideId, slideComments, onCommentsChange, onClose }: CommentPanelProps) => {
  const [showModal, setShowModal] = useState(false);
  const [commentContent, setCommentContent] = useState("");
  const [currentCommentId, setCurrentCommentId] = useState<number>(0);

  const refreshComments = async () => {
    if (slideId) {
      const data = await getSlideData(slideId);
      onCommentsChange(data.slideComments || []);
    }
  };

  const handleSubmit = async () => {
    if (commentContent === "" || !slideId) return;
    if (currentCommentId === 0) {
      await postComment(slideId, commentContent);
    } else {
      await putComment(currentCommentId, commentContent);
    }
    setShowModal(false);
    setCommentContent("");
    setCurrentCommentId(0);
    await refreshComments();
  };

  const handleDelete = async (commentId: number) => {
    const role = await checkUser();
    if (role !== "ADMIN_MAIN") {
      window.alert("삭제 권한이 없습니다.");
      return;
    }
    if (window.confirm("해당 코멘트를 삭제하시겠습니까?")) {
      await deleteComment(commentId);
      await refreshComments();
    }
  };

  return (
    <>
      <StCommentPanel onClick={(e) => e.stopPropagation()}>
        <StPanelHeader>
          <span>Comment</span>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <PlusCircleOutlined
              onClick={() => { setCurrentCommentId(0); setCommentContent(""); setShowModal(true); }}
              style={{ cursor: "pointer", color: "#aaa", fontSize: "1rem" }}
            />
            <StPanelClose onClick={onClose}>×</StPanelClose>
          </div>
        </StPanelHeader>
        <StPanelBody>
          {slideComments.length === 0 ? (
            <StPanelEmpty>댓글이 없습니다.</StPanelEmpty>
          ) : (
            slideComments.map((comment: any) => (
              <StPanelItem key={comment.slideCommentId}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <StPanelCommentContent>{comment.content}</StPanelCommentContent>
                  <StPanelCommentWriter>
                    {comment.writer}
                    {comment.anonymous === true && (
                      <StTempUserLabel>(링크 접근 사용자)</StTempUserLabel>
                    )}
                  </StPanelCommentWriter>
                </div>
                <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                  <EditOutlined
                    onClick={() => { setCurrentCommentId(comment.slideCommentId); setCommentContent(comment.content); setShowModal(true); }}
                    style={{ cursor: "pointer", color: "#aaa", fontSize: "0.9rem" }}
                  />
                  <DeleteOutlined
                    onClick={() => handleDelete(comment.slideCommentId)}
                    style={{ cursor: "pointer", color: "#aaa", fontSize: "0.9rem" }}
                  />
                </div>
              </StPanelItem>
            ))
          )}
        </StPanelBody>
      </StCommentPanel>

      <Modal
        title={currentCommentId === 0 ? "Add Comment" : "Edit Comment"}
        open={showModal}
        onOk={handleSubmit}
        onCancel={() => setShowModal(false)}
        width={480}
        // 화면 중앙이 아니라 우측 사이드메뉴 영역 하단에 고정 (viewport 우하단 앵커)
        // transform 사용 시 antd 등장 애니메이션과 충돌해 위로 튀는 현상이 있어 top/bottom으로만 배치
        style={{ position: "absolute", top: "auto", bottom: 220, right: 16, margin: 0 }}
      >
        <Input.TextArea
          value={commentContent}
          onChange={(e) => setCommentContent(e.target.value)}
          autoSize={{ minRows: 6, maxRows: 14 }}
          placeholder="코멘트를 입력하세요"
        />
      </Modal>
    </>
  );
};

// --- Styled Components ---

const StPanel = styled.div`
  position: absolute;
  bottom: 40px;
  right: 10px;
  width: 260px;
  max-height: 320px;
  background-color: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StCommentPanel = styled(StPanel)`
  margin-bottom: 1rem;
`;

const StPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: rgba(8, 9, 9, 0.55);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  flex-shrink: 0;
`;

const StPanelClose = styled.button`
  background: none;
  border: none;
  color: #aaa;
  font-size: 1.2rem;
  cursor: pointer;
  line-height: 1;
  padding: 0 2px;

  &:hover {
    color: white;
  }
`;

const StPanelBody = styled.div`
  padding: 6px 8px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`;

const StPanelEmpty = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.8rem;
  text-align: center;
  padding: 12px 0;
`;

const StPanelItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: white;

  &:last-child {
    border-bottom: none;
  }
`;

const StPanelCommentContent = styled.div`
  font-size: 0.82rem;
  word-break: break-word;
  line-height: 1.4;
`;

const StPanelCommentWriter = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 3px;
`;

const StTempUserLabel = styled.span`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.3);
`;
